import { useSnackbar } from 'notistack';
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef
} from 'react';

import { formDataInstance } from '@/common/axios';
import { getRestHeaders } from '@/common/utils/api';
import AuthUserStore from '@/common/utils/AuthUser';
import CONFIG from '@/config';
import PinInputModal from '@/RestoreComponent/PinInputModal';
import AccessDeniedModal from './AccessDeniedModal';
import { userHasAtLeastManagerRole } from '@/hooks/useElevatedRoleAction';
import { useIdleTimer } from 'react-idle-timer/legacy';
import { z } from 'zod';
import { AuthUserModel } from '@/models/AuthUserModel';
import axios from 'axios';

type PinCallbackType = (pin: string, isCancelled?: boolean) => void;
type LockContextType = {
  isPowLocked: boolean;
  lockPow: () => void;
  userLockPow: () => void;
  unlockPow: () => void;
  withPinEntry: (cb: PinCallbackType, promptMessage?: string) => void;
};

const LockContext = createContext<LockContextType>({
  isPowLocked: false,
  lockPow: () => {
    throw new Error('lock() not implemented');
  },
  userLockPow: () => {
    throw new Error('userLock() not implemented');
  },
  unlockPow: () => {
    throw new Error('unlock() not implemented');
  },
  withPinEntry: () => {
    throw new Error('withPinEntry() not implemented');
  }
});

const VerifySessionResponse = z.object({
  status: z.enum(['PIN_NEEDED', 'SIGNED_OUT', 'VALID_SESSION'] as const)
});

type VerifySessionStatus = z.infer<typeof VerifySessionResponse>['status'];

const IDLE_TIMEOUT_MS = 1000 * 60 * 15; // 15 minutes
const POLLING_INTERVAL_MS = 1000 * 30; // 30 seconds

function getAuthData(): {
  token: string | null;
  user: AuthUserModel | null;
} {
  return {
    token: AuthUserStore.getAuthToken(),
    user: AuthUserStore.getAuthUser()
  };
}

export async function poll(): Promise<VerifySessionStatus> {
  try {
    const authData = getAuthData();
    const { token, user } = authData;
    if (!token || !user) {
      return 'SIGNED_OUT';
    }
    const res = await axios.post(
      `${CONFIG.USERS_API_URL}/auth/v2/staff/verify-session`,
      {},
      {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      }
    );

    const parsed = VerifySessionResponse.parse(res.data.data);

    return parsed.status;
  } catch (_) {
    return 'SIGNED_OUT';
  }
}

export type LockProviderProps = { children: React.ReactNode };

export const LockProvider: React.FC<LockProviderProps> = ({ children }) => {
  const [isPowLocked, setIsLocked] = useState(false);
  const [isPinScreenVisible, setIsPinScreenVisible] = useState(false);
  const [isAccessDeniedModalVisible, setIsAccessDeniedModalVisible] =
    useState(false);
  const [pinCallback, setPinCallback] = useState<PinCallbackType>(null);
  const [promptMessage, setPromptMessage] = useState<string>('Enter your PIN');
  const { enqueueSnackbar } = useSnackbar();

  const { start, pause } = useIdleTimer({
    events: ['mousemove', 'click', 'keypress'],
    timeout: IDLE_TIMEOUT_MS,
    crossTab: true,
    leaderElection: true,
    onIdle: async () => {
      if (AuthUserStore.getAuthToken() && !isPowLocked) {
        pause();
        await onLock();
      }
    }
  });

  const pollRef = useRef<NodeJS.Timeout>();

  function resumeAfterUnlock() {
    start();
  }

  // If getLockStatus is 'locked' on page load, set it to 'refresh'
  useEffect(() => {
    const status = AuthUserStore.checkRefresh();
    if (status === 'locked') {
      setIsLocked(true);
    }
  }, []);

  // POW locks itself
  const lockPow = useCallback(() => {
    const authUser = AuthUserStore.getAuthUser();
    if (!authUser?.isGeneric) {
      setIsLocked(true);
    }
  }, []);

  // User manually locks POW
  const userLockPow = useCallback(async () => {
    const authUser = AuthUserStore.getAuthUser();
    if (!isPowLockedRef.current && !authUser?.isGeneric) {
      await formDataInstance
        .post(
          CONFIG.USERS_API_URL + '/auth/staff/pin-lock',
          {},
          { headers: getRestHeaders() }
        )
        .then(res => {
          const token = res?.data?.data?.token;
          AuthUserStore.setAuthToken(token);
          setIsLocked(true);
        })
        .catch(err => {
          console.log(err);
          enqueueSnackbar('There was an error locking POW', {
            variant: 'error'
          });
        });
    }
  }, []);

  const unlockPow = useCallback(() => {
    if (AuthUserStore.getLockStatus() === 'refresh') {
      console.log('Redirecting to home page');
      AuthUserStore.checkRefresh();
      window.location.reload();
    }
    setIsLocked(false);
    resumeAfterUnlock();
  }, []);

  const withPinEntry = useCallback((callback, promptMessage) => {
    const hasAtLeastManagerRole = userHasAtLeastManagerRole();
    setPinCallback(() => callback);
    if (!hasAtLeastManagerRole) {
      if (promptMessage) {
        setPromptMessage(promptMessage);
      }
      setIsAccessDeniedModalVisible(true);
    } else {
      setIsPinScreenVisible(true);
    }
  }, []);

  const isPowLockedRef = useRef<boolean>(isPowLocked);
  useEffect(() => {
    isPowLockedRef.current = isPowLocked;
  }, [isPowLocked]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.shiftKey && event.key === 'Escape') {
        event.preventDefault();
        userLockPow();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [lockPow]);

  const accessModalOkClick = () => {
    setIsAccessDeniedModalVisible(false);
    setIsPinScreenVisible(true);
  };

  async function onLock() {
    await formDataInstance
      .post(
        CONFIG.USERS_API_URL + '/auth/staff/pin-lock',
        {},
        { headers: getRestHeaders() }
      )
      .then(res => {
        const token = res?.data?.data?.token;
        AuthUserStore.setAuthToken(token);
        lockPow();
      });
  }

  const verify = useCallback(async () => {
    const status = await poll();
    if (status === 'VALID_SESSION') {
      if (isPowLocked) {
        unlockPow();
        start();
      }
      return;
    }

    if (status === 'PIN_NEEDED') {
      if (!isPowLocked) {
        pause();
        await onLock();
      }
    }

    if (status === 'SIGNED_OUT') {
      const { token } = getAuthData();
      if (token) {
        AuthUserStore.logout();
      }
    }
  }, [isPowLocked]);

  useEffect(() => {
    async function handler() {
      await verify();
      pollRef.current = setInterval(verify, POLLING_INTERVAL_MS);
    }

    // noinspection JSIgnoredPromiseFromCall
    handler();

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
    };
  }, []);

  return (
    <LockContext.Provider
      value={{
        isPowLocked,
        lockPow,
        userLockPow,
        unlockPow,
        withPinEntry
      }}
    >
      {children}
      {isPinScreenVisible && (
        <PinInputModal
          headerText={promptMessage}
          isOpen={isPinScreenVisible}
          onClose={() => setIsPinScreenVisible(false)}
          setPinCallback={pinCallback}
        />
      )}
      {isAccessDeniedModalVisible && (
        <AccessDeniedModal okClick={accessModalOkClick} />
      )}
    </LockContext.Provider>
  );
};

export const useLock = () => useContext(LockContext);
